import { useState, type FormEvent } from 'react';

export default function FormSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    if (charCount > 500) {
      setErrorMsg('お問合わせ内容は500文字以内で入力してください。');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const res = await fetch('https://readdy.ai/api/form/d8okvrnij7vns5otj0k0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
        setErrorMsg('送信に失敗しました。お手数ですが、お電話またはメールにてお問合わせください。');
      }
    } catch {
      setStatus('error');
      setErrorMsg('通信エラーが発生しました。時間をおいて再度お試しください。');
    }
  };

  return (
    <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto">
        {/* Header */}
        <div className="mb-12 md:mb-16">
          <p className="text-xs md:text-sm text-foreground-400 tracking-[0.15em] font-sans font-light mb-2">BANKCARD CHECK</p>
          <h3 className="text-xl md:text-3xl lg:text-4xl font-heading font-light text-foreground-950 tracking-[0.06em]">
            お問合わせフォーム
          </h3>
        </div>

        {/* Form */}
        <div className="max-w-3xl mx-auto">
          <form
            data-readdy-form=""
            onSubmit={handleSubmit}
            className="space-y-8"
          >
            {/* Company Name */}
            <div className="space-y-2">
              <label htmlFor="company" className="block text-xs md:text-sm text-foreground-600 tracking-[0.08em] font-sans font-light">
                会社名 <span className="text-accent-600">*</span>
              </label>
              <input
                id="company"
                name="company"
                type="text"
                required
                placeholder="例：株式会社〇〇"
                className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 tracking-[0.05em] font-sans font-light outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400/30 transition-all duration-300"
              />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <label htmlFor="name" className="block text-xs md:text-sm text-foreground-600 tracking-[0.08em] font-sans font-light">
                お名前 <span className="text-accent-600">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="例：山田 太郎"
                className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 tracking-[0.05em] font-sans font-light outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400/30 transition-all duration-300"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-xs md:text-sm text-foreground-600 tracking-[0.08em] font-sans font-light">
                メールアドレス <span className="text-accent-600">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="例：taro@example.com"
                className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 tracking-[0.05em] font-sans font-light outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400/30 transition-all duration-300"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label htmlFor="phone" className="block text-xs md:text-sm text-foreground-600 tracking-[0.08em] font-sans font-light">
                電話番号
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                placeholder="例：03-1234-5678"
                className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 tracking-[0.05em] font-sans font-light outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400/30 transition-all duration-300"
              />
            </div>

            {/* Inquiry Type */}
            <div className="space-y-2">
              <label htmlFor="inquiry_type" className="block text-xs md:text-sm text-foreground-600 tracking-[0.08em] font-sans font-light">
                お問合わせ種別 <span className="text-accent-600">*</span>
              </label>
              <select
                id="inquiry_type"
                name="inquiry_type"
                required
                className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 tracking-[0.05em] font-sans font-light outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400/30 transition-all duration-300 cursor-pointer appearance-none"
              >
                <option value="">選択してください</option>
                <option value="導入相談">導入相談</option>
                <option value="技術支援">技術支援</option>
                <option value="資料請求">資料請求</option>
                <option value="取材・パートナー">取材・パートナー</option>
                <option value="その他">その他</option>
              </select>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label htmlFor="message" className="block text-xs md:text-sm text-foreground-600 tracking-[0.08em] font-sans font-light">
                お問合わせ内容 <span className="text-accent-600">*</span>
                <span className="ml-2 text-foreground-400 font-light">（{charCount}/500文字）</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                required
                maxLength={500}
                placeholder="お問合わせ内容をご記入ください"
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 tracking-[0.05em] font-sans font-light outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400/30 transition-all duration-300 resize-none"
              />
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-800 text-accent-400 text-sm md:text-base tracking-[0.08em] font-sans font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {status === 'submitting' ? '送信中...' : '送信する'}
                <i className="ri-send-plane-line text-lg" />
              </button>
            </div>

            {/* Status Messages */}
            {status === 'success' && (
              <div className="p-4 rounded-lg bg-accent-100/80 border border-accent-300/60 text-accent-900 text-sm tracking-[0.06em] font-sans font-light">
                お問合わせを受け付けました。担当者より24時間以内にご連絡いたします。
              </div>
            )}
            {status === 'error' && errorMsg && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm tracking-[0.06em] font-sans font-light">
                {errorMsg}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}